import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Snackbar from '@mui/material/Snackbar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Collapse from '@mui/material/Collapse';
import { GridColDef } from '@mui/x-data-grid';
import { ResizableDataGrid } from './grid/ResizableDataGrid';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { requestsApi, RequestListItem, StrategicCategory, strategicCategoriesApi } from '../api/ppm';
import { canEditPage } from '../utils/rbac';
import { titleCase } from '../utils/format';
import { usersApi, DbUser } from '../api/client';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import { PageHeader } from './layout/PageHeader';
import { PageLoader } from './PageLoader';
import { StatCard } from './layout/StatCard';
import { TableCard } from './layout/TableCard';
import { ChartCard } from './charts/ChartCard';
import { NexusBarChart, NexusDonut } from './charts/NexusCharts';
import { tokens, chartColor } from '../theme/tokens';

const statusColor = (status: string) => {
  const s = (status || '').toUpperCase();
  if (s === 'APPROVED') return 'success';
  if (s === 'REJECTED') return 'error';
  if (s === 'IN_EVALUATION') return 'warning';
  if (s === 'SUBMITTED') return 'info';
  if (s === 'SUBMITTED_FOR_APPROVAL') return 'info';
  if (s === 'DRAFT') return 'default';
  return 'default';
};

const priorityColor = (priority: string) => {
  const p = (priority || '').toUpperCase();
  if (p === 'URGENT') return 'error';
  if (p === 'HIGH') return 'warning';
  if (p === 'MEDIUM') return 'info';
  return 'default';
};

const Requests: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { dbUser } = useCurrentUser();
  const canEdit = canEditPage(dbUser, 'requests');
  const [rows, setRows] = useState<RequestListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const [toastOpen, setToastOpen] = useState(false);
  const [toast, setToast] = useState<{ severity: 'success' | 'error'; message: string }>({ severity: 'success', message: '' });
  const [me, setMe] = useState<DbUser | null>(null);
  const [categories, setCategories] = useState<StrategicCategory[]>([]);

  useEffect(() => {
    usersApi.me().then((r) => setMe((r.data || null) as any)).catch(() => setMe(null));
  }, []);

  useEffect(() => {
    strategicCategoriesApi.getAll().then((r) => setCategories((r.data || []) as any)).catch(() => setCategories([]));
  }, []);

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<RequestListItem | null>(null);
  const [form, setForm] = useState({
    title: '',
    description: '',
    requestType: 'New Feature',
    status: 'SUBMITTED',
    priority: 'MEDIUM',
    ownerId: 'demo_user_pm',
    creatorId: 'demo_user_viewer',
    department: '',
    businessCase: '',
    estimatedCost: '',
    estimatedDuration: '',
    estimatedValue: '',
    roiImpact: '',
    strategicCategoryId: '',
    complexity: '',
  });

  const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(null);
  const [actionsRow, setActionsRow] = useState<RequestListItem | null>(null);
  const actionsOpen = Boolean(actionsAnchorEl);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const [topPanel, setTopPanel] = useState<null | 'total' | 'pending' | 'approved' | 'rejected'>(null);
  const [showAllPanel, setShowAllPanel] = useState(false);

  const fetchRows = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await requestsApi.getAll();
      setRows(res.data || []);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep-link support: /requests?openId=<id>
  useEffect(() => {
    const openId = searchParams.get('openId');
    if (!openId) return;
    const found = rows.find((r) => String(r.id) === String(openId));
    if (!found) return;
    setActionsRow(found);
    setDetailsOpen(true);
    // leave params in place; user can refresh/share
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, searchParams]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const title = (r.title || '').toLowerCase();
      const creator = (r.Creator?.email || r.Creator?.displayName || '').toLowerCase();
      const owner = (r.Owner?.email || r.Owner?.displayName || '').toLowerCase();
      const dept = (r.department || '').toLowerCase();
      const status = (r.status || '').toLowerCase();
      return title.includes(q) || creator.includes(q) || owner.includes(q) || dept.includes(q) || status.includes(q);
    });
  }, [rows, search]);

  const summary = useMemo(() => {
    const total = rows.length;
    const pending = rows.filter((r) => ['SUBMITTED', 'IN_EVALUATION', 'SUBMITTED_FOR_APPROVAL'].includes(String(r.status || '').toUpperCase())).length;
    const approved = rows.filter((r) => String(r.status || '').toUpperCase() === 'APPROVED').length;
    const rejected = rows.filter((r) => String(r.status || '').toUpperCase() === 'REJECTED').length;
    return { total, pending, approved, rejected };
  }, [rows]);

  const statusMix = useMemo(() => {
    const map = new Map<string, number>();
    (rows || []).forEach((r) => {
      const s = String(r.status || 'UNKNOWN').toUpperCase();
      map.set(s, (map.get(s) || 0) + 1);
    });
    return Array.from(map.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [rows]);

  const priorityMix = useMemo(() => {
    const order = ['URGENT', 'HIGH', 'MEDIUM', 'LOW', 'NONE'];
    const map = new Map<string, number>();
    (rows || []).forEach((r) => {
      const p = String(r.priority || '').toUpperCase() || 'NONE';
      map.set(p, (map.get(p) || 0) + 1);
    });
    return order.map((name) => ({ name, value: map.get(name) || 0 }));
  }, [rows]);

  const deptTop = useMemo(() => {
    const map = new Map<string, number>();
    (rows || []).forEach((r) => {
      const d = String(r.department || '—');
      map.set(d, (map.get(d) || 0) + 1);
    });
    return Array.from(map.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [rows]);

  const approve = async (id: string, convertToProject: boolean) => {
    try {
      await requestsApi.approve(id, convertToProject);
      setToast({ severity: 'success', message: convertToProject ? 'Approved + created project' : 'Approved request' });
      setToastOpen(true);
      fetchRows();
    } catch (e: any) {
      setToast({ severity: 'error', message: e?.response?.data?.message || e?.message || 'Approve failed' });
      setToastOpen(true);
    }
  };

  const openReject = (id: string) => {
    setActiveId(id);
    setRejectReason('');
    setRejectOpen(true);
  };

  const submitReject = async () => {
    if (!activeId) return;
    try {
      await requestsApi.reject(activeId, rejectReason || undefined);
      setRejectOpen(false);
      setToast({ severity: 'success', message: 'Rejected request' });
      setToastOpen(true);
      fetchRows();
    } catch (e: any) {
      setToast({ severity: 'error', message: e?.response?.data?.message || e?.message || 'Reject failed' });
      setToastOpen(true);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({
      title: '',
      description: '',
      requestType: 'New Feature',
      status: 'SUBMITTED',
      priority: 'MEDIUM',
      ownerId: me?.id || '',
      creatorId: me?.id || '',
      department: '',
      businessCase: '',
      estimatedCost: '',
      estimatedDuration: '',
      estimatedValue: '',
      roiImpact: '',
      strategicCategoryId: categories[0]?.id ? String(categories[0].id) : '',
      complexity: '',
    });
    setEditOpen(true);
  };

  const openEdit = (r: RequestListItem) => {
    setEditing(r);
    setForm({
      title: r.title || '',
      description: r.description || '',
      requestType: r.requestType || 'New Feature',
      status: r.status || 'SUBMITTED',
      priority: (r.priority || 'MEDIUM') as any,
      ownerId: r.Owner?.id || 'demo_user_pm',
      creatorId: r.Creator?.id || 'demo_user_viewer',
      department: r.department || '',
      businessCase: r.businessCase || '',
      estimatedCost: r.estimatedCost != null ? String(r.estimatedCost) : '',
      estimatedDuration: r.estimatedDuration != null ? String(r.estimatedDuration) : '',
      estimatedValue: r.estimatedValue != null ? String(r.estimatedValue) : '',
      roiImpact: r.roiImpact != null ? String(r.roiImpact) : '',
      strategicCategoryId: r.strategicCategoryId ? String(r.strategicCategoryId) : '',
      complexity: r.complexity != null ? String(r.complexity) : '',
    });
    setEditOpen(true);
  };

  const save = async () => {
    try {
      if (!form.title.trim() || !form.description.trim()) {
        setToast({ severity: 'error', message: 'Title and description are required' });
        setToastOpen(true);
        return;
      }
      // Only require estimated cost for admins (they can convert requests into projects).
      if (canEdit && !(Number(form.estimatedCost || 0) > 0)) {
        setToast({ severity: 'error', message: 'Estimated Cost is required (needed to convert a request into a project)' });
        setToastOpen(true);
        return;
      }

      const numOrUndef = (v: string) => (v.trim() === '' ? undefined : Number(v));

      const payload: any = {
        title: form.title.trim(),
        description: form.description.trim(),
        requestType: form.requestType.trim() ? form.requestType.trim() : undefined,
        status: canEdit ? form.status : 'SUBMITTED',
        priority: form.priority,
        department: form.department.trim() ? form.department.trim() : undefined,
        businessCase: form.businessCase.trim() ? form.businessCase.trim() : undefined,
        estimatedCost: form.estimatedCost.trim() ? Number(form.estimatedCost || 0) : undefined,
        estimatedDuration: numOrUndef(form.estimatedDuration),
        estimatedValue: numOrUndef(form.estimatedValue),
        roiImpact: numOrUndef(form.roiImpact),
        strategicCategoryId: form.strategicCategoryId?.trim() ? form.strategicCategoryId.trim() : undefined,
        complexity: numOrUndef(form.complexity),
        submittedDate: new Date(),
      };

      // Owner/Creator are forced server-side to the authenticated user (admins included).

      if (editing) {
        await requestsApi.update(editing.id, payload);
        setToast({ severity: 'success', message: 'Request updated' });
      } else {
        await requestsApi.create(payload);
        setToast({ severity: 'success', message: 'Request created' });
      }
      setToastOpen(true);
      setEditOpen(false);
      fetchRows();
    } catch (e: any) {
      setToast({ severity: 'error', message: e?.response?.data?.message || e?.message || 'Save failed' });
      setToastOpen(true);
    }
  };

  const remove = async (r: RequestListItem) => {
    try {
      // eslint-disable-next-line no-alert
      const ok = window.confirm(`Delete request "${r.title}"?`);
      if (!ok) return;
      await requestsApi.remove(r.id);
      setToast({ severity: 'success', message: 'Request deleted' });
      setToastOpen(true);
      fetchRows();
    } catch (e: any) {
      setToast({ severity: 'error', message: e?.response?.data?.message || e?.message || 'Delete failed' });
      setToastOpen(true);
    }
  };

  const cols: GridColDef[] = [
    {
      field: 'title',
      headerName: 'Request',
      flex: 1,
      minWidth: 240,
      renderCell: (params) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 650 }} noWrap title={params.value}>
            {params.value}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {params.row.department ? `Dept: ${params.row.department}` : '—'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value ? titleCase(String(params.value)) : '—'}
          color={statusColor(params.value) as any}
          variant="outlined"
          sx={{ height: 22, fontWeight: 700, letterSpacing: 0.2 }}
        />
      ),
    },
    {
      field: 'priority',
      headerName: 'Priority',
      width: 130,
      renderCell: (params) =>
        params.value ? (
          <Chip
            size="small"
            label={titleCase(String(params.value))}
            color={priorityColor(params.value) as any}
            variant="outlined"
            sx={{ height: 22, fontWeight: 700, letterSpacing: 0.2 }}
          />
        ) : (
          <span>—</span>
        ),
    },
    {
      field: 'evaluationScore',
      headerName: 'Score',
      width: 110,
      valueGetter: (params) => (typeof params.row.evaluationScore === 'number' ? params.row.evaluationScore : null),
    },
    {
      field: 'creator',
      headerName: 'Creator',
      minWidth: 160,
      flex: 1,
      valueGetter: (params) => params.row.Creator?.email || params.row.Creator?.displayName || '—',
    },
    ...(canEdit
      ? ([
          {
            field: 'actions',
            headerName: 'Admin',
            width: 90,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setActionsAnchorEl(e.currentTarget);
                  setActionsRow(params.row);
                }}
                aria-label="Actions"
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            ),
          } as GridColDef,
        ] as GridColDef[])
      : []),
  ];

  return (
    <PageLoader loading={loading}>
      <Container maxWidth="xl" sx={{ pt: 1, pb: 3 }}>
      <PageHeader
        title="Requests"
        subtitle={canEdit ? "Review, score, approve/reject, and optionally convert approved requests into projects." : "Create and track your requests."}
        actions={
          <>
            <TextField
              size="small"
              placeholder="Search (title, creator, owner, dept, status)…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ width: { xs: '100%', sm: 520 } }}
            />
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
              <Button variant="contained" onClick={openCreate} sx={{ borderRadius: 2 }}>
                New Request
              </Button>
              <Button variant="outlined" onClick={fetchRows} sx={{ borderRadius: 2 }}>
                Refresh
              </Button>
            </Stack>
          </>
        }
      />

      {error ? (
        <Alert severity="error" variant="filled" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Total"
            value={summary.total}
            subtitle="Loaded"
            accent={tokens.color.accent}
            onClick={() => {
              setShowAllPanel(false);
              setTopPanel((p) => (p === 'total' ? null : 'total'));
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Pending"
            value={summary.pending}
            subtitle="In intake"
            accent={tokens.color.warning}
            onClick={() => {
              setShowAllPanel(false);
              setTopPanel((p) => (p === 'pending' ? null : 'pending'));
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Approved"
            value={summary.approved}
            subtitle="Ready"
            accent={tokens.color.success}
            onClick={() => {
              setShowAllPanel(false);
              setTopPanel((p) => (p === 'approved' ? null : 'approved'));
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Rejected"
            value={summary.rejected}
            subtitle="Declined"
            accent={tokens.color.danger}
            onClick={() => {
              setShowAllPanel(false);
              setTopPanel((p) => (p === 'rejected' ? null : 'rejected'));
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Collapse in={!!topPanel} unmountOnExit>
            <TableCard
              title={topPanel === 'total' ? 'Requests' : topPanel === 'pending' ? 'Pending requests' : topPanel === 'approved' ? 'Approved requests' : 'Rejected requests'}
              actions={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button size="small" variant="outlined" onClick={() => setShowAllPanel((s) => !s)} sx={{ borderRadius: 2 }}>
                    {showAllPanel ? 'Show less' : 'Show all'}
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => setTopPanel(null)} sx={{ borderRadius: 2 }}>
                    Close
                  </Button>
                </Stack>
              }
            >
              {(() => {
                const rowsForPanel =
                  topPanel === 'total'
                    ? rows
                    : topPanel === 'pending'
                      ? rows.filter((r) => ['SUBMITTED', 'IN_EVALUATION', 'SUBMITTED_FOR_APPROVAL'].includes(String(r.status || '').toUpperCase()))
                      : topPanel === 'approved'
                        ? rows.filter((r) => String(r.status || '').toUpperCase() === 'APPROVED')
                        : rows.filter((r) => String(r.status || '').toUpperCase() === 'REJECTED');

                if (!rowsForPanel.length) {
                  return (
                    <Alert severity="info" variant="outlined">
                      No results.
                    </Alert>
                  );
                }

                return (
                  <Stack spacing={1}>
                    {rowsForPanel.slice(0, showAllPanel ? 1000 : 5).map((r) => (
                      <Box
                        key={r.id}
                        sx={{ p: 1, borderRadius: `${tokens.radius.md}px`, border: `1px solid ${tokens.color.border}`, cursor: 'pointer', '&:hover': { backgroundColor: tokens.color.surfaceMuted } }}
                        onClick={() => {
                          setActionsRow(r);
                          setActionsAnchorEl(null);
                          setDetailsOpen(true);
                        }}
                      >
                        <Typography sx={{ fontWeight: 650 }} noWrap title={r.title || ''}>
                          {r.title || 'Untitled'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {r.department || '—'} • {titleCase(String(r.status || '—'))} • {r.priority ? titleCase(String(r.priority)) : '—'}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                );
              })()}
            </TableCard>
          </Collapse>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <ChartCard title="Status mix" subtitle="Requests by status" height={260}>
            <NexusDonut
              data={statusMix.map((d, i) => ({ name: titleCase(d.name), value: d.value, color: chartColor(i) }))}
              valueFormatter={(v) => String(v)}
              height={200}
              centerLabel="Total"
              centerValue={String(summary.total)}
            />
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <ChartCard title="Priority" subtitle="Requests by priority" height={260}>
            <NexusBarChart
              data={priorityMix.map((d) => ({ name: titleCase(d.name), value: d.value }))}
              xKey="name"
              series={[{ key: 'value', name: 'Requests', color: tokens.color.accent }]}
              valueFormatter={(v) => String(v)}
              height={220}
            />
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <ChartCard title="Top departments" subtitle="Where requests come from" height={260}>
            <NexusBarChart
              data={deptTop.map((d) => ({ name: d.name, value: d.value }))}
              xKey="name"
              series={[{ key: 'value', name: 'Requests', color: tokens.color.accent }]}
              valueFormatter={(v) => String(v)}
              height={220}
            />
          </ChartCard>
        </Grid>
      </Grid>

      <TableCard
        title="Request list"
        subtitle="Click a row for details. Admin actions are in the overflow menu."
        actions={loading ? <CircularProgress size={18} /> : null}
      >
        <div style={{ height: 580, width: '100%' }}>
          <ResizableDataGrid
            storageKey="gridWidths.requests"
            enableDensityControls
            rows={filtered}
            columns={cols}
            getRowId={(r) => r.id}
            disableRowSelectionOnClick
            onRowClick={(p) => {
              setActionsRow(p.row);
              setActionsAnchorEl(null);
              setDetailsOpen(true);
            }}
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 10, page: 0 } },
            }}
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeaders': { backgroundColor: tokens.color.surfaceMuted },
              '& .MuiDataGrid-row:hover': { backgroundColor: tokens.color.surfaceMuted },
            }}
          />
        </div>
      </TableCard>

      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Reject request</DialogTitle>
        <DialogContent>
          <TextField
            label="Reason (optional)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setRejectOpen(false)}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={submitReject}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'Edit Request' : 'Create Request'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 1 }}>
          <TextField label="Title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} fullWidth />
          <TextField
            label="Description"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            fullWidth
            multiline
            minRows={3}
          />
          <TextField label="Department" value={form.department} onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))} fullWidth />
          <TextField
            label="Request Type"
            value={form.requestType}
            onChange={(e) => setForm((f) => ({ ...f, requestType: e.target.value }))}
            fullWidth
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            {canEdit ? (
              <TextField label="Status" select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} fullWidth>
                {['DRAFT', 'SUBMITTED', 'SUBMITTED_FOR_APPROVAL', 'IN_EVALUATION', 'APPROVED', 'REJECTED'].map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>
            ) : null}
            <TextField label="Priority" select value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))} fullWidth>
              {['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'].map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          {canEdit ? (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Alert severity="info" variant="outlined" sx={{ width: '100%' }}>
                Owner/Creator are set automatically to <b>{me?.username || me?.email || 'you'}</b>.
              </Alert>
            </Box>
          ) : null}
          <TextField
            label="Business Case"
            value={form.businessCase}
            onChange={(e) => setForm((f) => ({ ...f, businessCase: e.target.value }))}
            fullWidth
            multiline
            minRows={2}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              label="Estimated Cost"
              value={form.estimatedCost}
              onChange={(e) => setForm((f) => ({ ...f, estimatedCost: e.target.value }))}
              fullWidth
              required={canEdit}
              helperText={canEdit ? 'Required (used as project budget when approving + converting to a project).' : 'Optional.'}
            />
            <TextField label="Estimated Value" value={form.estimatedValue} onChange={(e) => setForm((f) => ({ ...f, estimatedValue: e.target.value }))} fullWidth />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField label="ROI Impact (0-10)" value={form.roiImpact} onChange={(e) => setForm((f) => ({ ...f, roiImpact: e.target.value }))} fullWidth />
            <TextField
              select
              label="Strategic Alignment"
              value={form.strategicCategoryId}
              onChange={(e) => setForm((f) => ({ ...f, strategicCategoryId: e.target.value }))}
              fullWidth
            >
              {categories.length ? null : (
                <MenuItem value="">
                  <em>No alignments configured</em>
                </MenuItem>
              )}
              {categories.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField label="Complexity (1-5)" value={form.complexity} onChange={(e) => setForm((f) => ({ ...f, complexity: e.target.value }))} fullWidth />
          </Box>
          <TextField label="Estimated Duration (days)" value={form.estimatedDuration} onChange={(e) => setForm((f) => ({ ...f, estimatedDuration: e.target.value }))} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setEditOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={save}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={toastOpen} autoHideDuration={2500} onClose={() => setToastOpen(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={toast.severity} variant="filled">
          {toast.message}
        </Alert>
      </Snackbar>

      <Menu
        anchorEl={actionsAnchorEl}
        open={actionsOpen}
        onClose={() => {
          setActionsAnchorEl(null);
          setActionsRow(null);
        }}
      >
        <MenuItem
          onClick={() => {
            if (actionsRow) openEdit(actionsRow);
            setActionsAnchorEl(null);
          }}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (actionsRow) approve(actionsRow.id, false);
            setActionsAnchorEl(null);
          }}
        >
          Approve
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (actionsRow) approve(actionsRow.id, true);
            setActionsAnchorEl(null);
          }}
        >
          Approve + Project
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (actionsRow) openReject(actionsRow.id);
            setActionsAnchorEl(null);
          }}
        >
          Reject…
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (actionsRow) remove(actionsRow);
            setActionsAnchorEl(null);
          }}
          sx={{ color: 'error.main' }}
        >
          Delete
        </MenuItem>
      </Menu>

      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Request</DialogTitle>
        <DialogContent>
          {actionsRow ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip size="small" label={actionsRow.status ? titleCase(String(actionsRow.status)) : '—'} variant="outlined" sx={{ height: 22, fontWeight: 700 }} />
                <Chip size="small" label={actionsRow.priority ? titleCase(String(actionsRow.priority)) : '—'} variant="outlined" sx={{ height: 22, fontWeight: 700 }} />
                {typeof actionsRow.evaluationScore === 'number' ? (
                  <Chip size="small" label={`Score ${actionsRow.evaluationScore}`} variant="outlined" sx={{ height: 22, fontWeight: 700 }} />
                ) : null}
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {actionsRow.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {actionsRow.department ? `Dept: ${actionsRow.department}` : null}
              </Typography>
              <Typography variant="body2">{actionsRow.description}</Typography>
              {actionsRow.businessCase ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 700 }}>
                    Business case
                  </Typography>
                  <Typography variant="body2">{actionsRow.businessCase}</Typography>
                </Box>
              ) : null}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          {canEdit && actionsRow ? (
            <>
              <Button variant="outlined" onClick={() => openEdit(actionsRow)}>
                Edit
              </Button>
              <Button variant="outlined" onClick={() => approve(actionsRow.id, false)}>
                Approve
              </Button>
              <Button variant="contained" onClick={() => approve(actionsRow.id, true)}>
                Approve + Project
              </Button>
              <Button color="error" variant="outlined" onClick={() => openReject(actionsRow.id)}>
                Reject…
              </Button>
              <Button color="error" variant="outlined" onClick={() => remove(actionsRow)}>
                Delete
              </Button>
            </>
          ) : null}
          <Button variant="contained" onClick={() => setDetailsOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      </Container>
    </PageLoader>
  );
};

export default Requests;


